import { GymTypes } from 'fitbeat.models';
import { WarmupWarmdownConfig } from '../../../config/WarmupwarmdownConfig';
import { WorkoutType } from '../../../config/workouttype';
import { CardioOrResistance, Exercise } from '../../../entities/exercise/exercise';
import { WorkoutSettings } from '../../../entities/user/workoutsettings';
import { ExerciseSelection } from '../../../entities/workout/exerciseselection';
import { UserExerciseSession } from '../../../entities/workoutsession';
import { IExerciseRepository } from '../../../persistence';
import { SpecialFormatExerciseSelector } from './SpecialFormatExerciseSelector';

export class WarmupAndWarmdownSelector {

    public static warmupWarmdownExerciseSelection(session: UserExerciseSession,
                                                  warmupsAndWarmdowns: Exercise[],
                                                  workoutSettings: WorkoutSettings)
        : ExerciseSelection | undefined {

        if (!session.isWarmupOrWarmdown) {
            return undefined;
        }

        const selection = ExerciseSelection.fromUserExerciseSession(session);

        // adust format if settings have changed
        selection.format = WarmupWarmdownConfig.getCorrectWarmdupdownFormatForSetting(
            session.format, workoutSettings.cardioOrResistance);

        const exercise = SpecialFormatExerciseSelector.filterFirstExerciseForFormatAndBodyGroup(
            warmupsAndWarmdowns, selection, workoutSettings);

        if (!exercise) {
            return undefined; // will run normal exercise for the zone, not a warmup, as a fallback
        }
        selection.exercise = exercise;

        return selection;
    }

    public static async getWarmupsWarmdownsForToday(
        exerciseRepository: IExerciseRepository,
        gymType: GymTypes, date: Date,
        instructor?: string, workoutCount?: number, settings?: WorkoutSettings)
        : Promise<Exercise[]> {
        const allExercises = await exerciseRepository.getAll();
        const warmupWarmdownSelector = new WarmupAndWarmdownSelector();
        const uniqueWarmupsAndWarmdowns = warmupWarmdownSelector.getUniqueWarmupWarmdownFiltersFor(
            date, allExercises, gymType, instructor, workoutCount, settings);
        return uniqueWarmupsAndWarmdowns;
    }

    public getUniqueWarmupWarmdownFiltersFor(
        date: Date, allExercises: Exercise[],
        gymType: GymTypes = GymTypes.Indoor,
        instructor?: string, workoutCount?: number, settings?: WorkoutSettings):
        Exercise[] {
        const randomiseSelection = false;
        const allExercisesExcludingTutorialWarmups = this.removeTutorialWarmups(allExercises);
        const nonRandomSet = SpecialFormatExerciseSelector.getRandomSetMatchingFormatAndBodyGroups(
            allExercisesExcludingTutorialWarmups, gymType, date, WarmupWarmdownConfig.isWarmupOrWarmdown,
            randomiseSelection, instructor);
        const isTutorialNeeded = typeof workoutCount !== 'undefined' ?
          WarmupWarmdownConfig.isTutorialWarmupNeeded(workoutCount) : false;
        if (isTutorialNeeded) {
          return this.replaceWithTutorialWarmup(allExercises, nonRandomSet, workoutCount!, settings);
        }
        return nonRandomSet;
    }

    public amountOfWarmdownZones(workoutType: WorkoutType): number {
        if (workoutType > 13) {
            return 1;
        }
        return 0;
    }

    private removeTutorialWarmups(exercises: Exercise[]) {
        return exercises.filter((ex) => !WarmupWarmdownConfig.isTutorialWarmup(String(ex.uniqueReferenceNumber)));
    }

    private replaceWithTutorialWarmup(
        allExercises: Exercise[],
        nonRandomSet: Exercise[],
        workoutCount: number,
        settings?: WorkoutSettings) {
            const isBalanceWorkout = settings?.cardioOrResistance === CardioOrResistance.Balance;
            const tutorialWarmupConfig = isBalanceWorkout ?
                WarmupWarmdownConfig.getBalanceTutorialWarmup(workoutCount) :
                WarmupWarmdownConfig.getTutorialWarmup(workoutCount);
            if (tutorialWarmupConfig) {
                const ref = tutorialWarmupConfig.ref;
                const tutorialWarmup = allExercises.find((x) => x.uniqueReferenceNumber.toString() === ref);
                if (tutorialWarmup) {
                    nonRandomSet = nonRandomSet.filter((ex) =>
                        ex.exerciseFormats.some((ef) =>
                            WarmupWarmdownConfig.allWarmupFormats().indexOf(ef) === -1));

                    nonRandomSet = [tutorialWarmup, ...nonRandomSet];
                }
            }
            return nonRandomSet;
    }
}
